import { useCallback, useEffect, useRef, useState } from "react";

import { GameHistoryStatus } from "../api/gameHistory";
import { GameStreamEvent } from "../api/gameStream";
import { useGameWrites } from "../api/useGameWrites";
import { GameFullResponse } from "../types/gameFull";
import { TagPlayerOverrides } from "../utils/buildTagPayload";
import {
  enqueueFills,
  enqueuePlayerChange,
  resolveQueuedTag,
  TagQueueEntry,
  takeNextEntry,
} from "../utils/tagQueue";

/**
 * Sends the tag queue (utils/tagQueue.ts) one tag at a time, so with the
 * tagger's own saves no more than two tags are in flight (like the event log
 * editor's batches). Each tag is built from the latest data when it's sent.
 * A pitch whose own tag is saving waits, so the two never race: omnibus
 * keeps whichever tag for a pitch it queues last.
 *
 * Fills start when an at bat ends. Usually one tagger has the game open; a
 * second page would send the same fills again, which is harmless. Leaving
 * the page drops what's left.
 *
 * Nothing is queued or sent until the game's older pitches have loaded, or a
 * fill would miss the start of a long at bat. Requests made meanwhile wait,
 * also while a failed load waits for Load More.
 */
export const useTagQueue = ({
  data,
  writes,
  savingIds,
  historyStatus,
}: {
  data: GameFullResponse;
  writes: ReturnType<typeof useGameWrites>;
  /** Pitches whose own tags are saving */
  savingIds: ReadonlySet<string>;
  historyStatus: GameHistoryStatus;
}) => {
  const dataRef = useRef(data);
  const writesRef = useRef(writes);
  const savingRef = useRef(savingIds);
  const historyRef = useRef(historyStatus);
  useEffect(() => {
    dataRef.current = data;
    writesRef.current = writes;
    savingRef.current = savingIds;
    historyRef.current = historyStatus;
  });

  // Queue changes asked for before the history loaded, applied once it has
  const deferred = useRef<((current: TagQueueEntry[]) => TagQueueEntry[])[]>(
    [],
  );
  const queue = useRef<TagQueueEntry[]>([]);
  const inFlight = useRef<TagQueueEntry | null>(null);
  // Settles when the in-flight tag's request has returned
  const inFlightSend = useRef<Promise<void> | null>(null);
  const isRunning = useRef(false);
  const filledPlateAppearances = useRef(new Set<string>());
  const drainWaiters = useRef<(() => void)[]>([]);
  // Mirrors the queue for rendering, and wakes the worker when it changes
  const [queued, setQueued] = useState<{
    queue: TagQueueEntry[];
    inFlight: TagQueueEntry | null;
  }>({ queue: [], inFlight: null });

  const publish = useCallback(
    () => setQueued({ queue: queue.current, inFlight: inFlight.current }),
    [],
  );

  const send = async (entry: TagQueueEntry) => {
    const latest = dataRef.current;
    const action = resolveQueuedTag(latest, entry);
    if (action === "skip") return;
    const overrides = entry.kind === "players" ? entry.players : {};
    for (const attempt of [1, 2]) {
      try {
        await writesRef.current.sendQueuedTag(
          latest,
          entry.activityId,
          action,
          overrides,
        );
        return;
      } catch (error) {
        // Background tags fail silently otherwise: no toast
        console.warn(
          `[scorekeeping] queued tag for ${entry.activityId} failed (attempt ${attempt})`,
          error,
        );
      }
    }
  };

  const resolveDrainWaiters = () => {
    drainWaiters.current.forEach((resolve) => resolve());
    drainWaiters.current = [];
  };

  const run = async () => {
    if (isRunning.current || historyRef.current !== "done") return;
    isRunning.current = true;
    try {
      for (;;) {
        const [entry, rest] = takeNextEntry(queue.current, (id) =>
          savingRef.current.has(id),
        );
        // Empty, or every pitch left is waiting on its own save
        if (!entry) break;
        queue.current = rest;
        inFlight.current = entry;
        inFlightSend.current = send(entry);
        publish();
        await inFlightSend.current;
        inFlight.current = null;
        inFlightSend.current = null;
        publish();
      }
    } finally {
      isRunning.current = false;
    }
    if (queue.current.length === 0) resolveDrainWaiters();
  };

  // Once the history is in, what was asked for meanwhile is queued against
  // the whole game. Until then it waits rather than being sent short; End
  // Game doesn't wait for a failed load.
  useEffect(() => {
    if (historyStatus === "error") resolveDrainWaiters();
    if (historyStatus !== "done" || deferred.current.length === 0) return;
    queue.current = deferred.current.reduce(
      (current, next) => next(current),
      queue.current,
    );
    deferred.current = [];
    publish();
  }, [historyStatus, publish]);

  // Runs after each render, so the worker reads the data that queued it.
  // A finished save re-runs it for the pitches that were waiting on it.
  useEffect(() => {
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queued, savingIds, historyStatus]);

  const update = useCallback(
    (next: (current: TagQueueEntry[]) => TagQueueEntry[]) => {
      if (!writesRef.current.canWrite) return;
      if (historyRef.current !== "done") {
        deferred.current.push(next);
        return;
      }
      queue.current = next(queue.current);
      publish();
    },
    [publish],
  );

  /** New hitter/pitcher for an at bat: its pitches go first. */
  const enqueuePlayers = (
    plateAppearanceId: string,
    players: TagPlayerOverrides,
    options?: { exclude?: string[] },
  ) =>
    update((current) =>
      enqueuePlayerChange(
        current,
        dataRef.current,
        plateAppearanceId,
        players,
        options,
      ),
    );

  /**
   * For a caller about to send a pitch's tag itself (Post At-Bat Update),
   * with everything a queued one would: drops the pitch from the queue, and
   * resolves once a request the queue already sent for it has returned.
   * Omnibus queues a tag before responding, so the caller's lands last.
   */
  const takeOver = async (activityId: string) => {
    queue.current = queue.current.filter(
      (entry) => entry.activityId !== activityId,
    );
    publish();
    if (inFlight.current?.activityId === activityId) {
      await inFlightSend.current;
    }
  };

  /** End Game: fills the open at bat, then waits for the queue to finish. */
  const finish = (plateAppearanceId: string | null | undefined) => {
    if (plateAppearanceId) {
      update((current) =>
        enqueueFills(current, dataRef.current, plateAppearanceId, {
          isFinal: true,
        }),
      );
    }
    const isIdle =
      queue.current.length === 0 &&
      !inFlight.current &&
      deferred.current.length === 0;
    if (isIdle || historyRef.current === "error") return Promise.resolve();
    return new Promise<void>((resolve) => drainWaiters.current.push(resolve));
  };

  /**
   * Fills an at bat's untagged pitches when it ends while the page watches.
   * Called before the event reaches the data, so the data still shows the at
   * bat as it was. One already ended there was filled when it ended: the
   * service re-sends the latest at bat on every (re)connect, and more updates
   * after re-tags, and the dropped fills leave its pitches looking untagged.
   */
  const receiveStreamEvent = useCallback(
    (event: GameStreamEvent) => {
      if (event.type !== "plate-appearance-updated") return;
      const { plateAppearanceId, status, activityIds } = event.plateAppearance;
      if (status !== "ended") return;
      // Before this game's snapshot is in, it's a replay of what it holds
      if (dataRef.current.game.gameId !== event.gameId) return;
      // An at bat ending live is always in the data first, open (the one the
      // page seats before the service starts it, at the latest). One that
      // isn't was never watched: a replay the data hasn't caught up to.
      const known = dataRef.current.plateAppearances.find(
        (pa) => pa.plateAppearanceId === plateAppearanceId,
      );
      if (!known || known.status === "ended") return;
      // Two updates can arrive before the data catches up
      if (filledPlateAppearances.current.has(plateAppearanceId)) return;
      filledPlateAppearances.current.add(plateAppearanceId);
      update((current) => {
        // The event may not be in the data yet: pick the pitches as of its end
        const ended: GameFullResponse = {
          ...dataRef.current,
          plateAppearances: dataRef.current.plateAppearances.map((pa) =>
            pa.plateAppearanceId === plateAppearanceId
              ? { ...pa, status, activityIds }
              : pa,
          ),
        };
        return enqueueFills(current, ended, plateAppearanceId);
      });
    },
    [update],
  );

  return {
    /** Queued pitches (first) and the one being sent */
    queued,
    enqueuePlayers,
    takeOver,
    finish,
    receiveStreamEvent,
  };
};
